import * as React from 'react';
import classNames from 'classnames';
import { Button } from '@/components/shared';
import { CmsFilterCategory } from '@/types/filterCategory';
import styles from './BlogFilterRow.module.scss';

export interface BlogFilterRowProps {
	readonly className?: string;
	/** The categories the block is configured with, in the order the editor picked them */
	readonly categories: readonly CmsFilterCategory[];
	/**
	 * Whether the all articles pill reads as chosen. Not the same as nothing being filtered:
	 * on arrival no pill is active, and it only lights up once a visitor picks it.
	 */
	readonly isShowingAllArticles?: boolean;
	/** Keys of the categories filtered on. Empty means nothing is narrowed down. */
	readonly selectedKeys: readonly string[];
	readonly allArticlesLabel: string;
	/** Names the row for screen readers, since the buttons alone do not say what they filter */
	readonly ariaLabel: string;
	readonly onToggleCategory: (key: string) => void;
	readonly onShowAllArticles: () => void;
}

/**
 * The filter row above the article grid, one pill per category the block is configured with.
 *
 * Every pill is the shared outline Button, so a selected pill only has to change its fill.
 */
export const BlogFilterRow: React.FunctionComponent<BlogFilterRowProps> = ({
	className,
	categories,
	isShowingAllArticles,
	selectedKeys,
	allArticlesLabel,
	ariaLabel,
	onToggleCategory,
	onShowAllArticles,
}) => {
	if (categories.length === 0) {
		return null;
	}

	return (
		<ul className={classNames(styles.row, className)} aria-label={ariaLabel}>
			<li className={styles.item}>
				{/* Reads as chosen only once a visitor has picked it, not on arrival */}
				<Button
					className={styles.pill}
					variant={isShowingAllArticles ? 'primary' : 'outline'}
					aria-pressed={isShowingAllArticles}
					onClick={onShowAllArticles}
				>
					<span className={styles.label}>{allArticlesLabel}</span>
				</Button>
			</li>
			{categories.map((category) => {
				const isSelected = selectedKeys.includes(category.key);

				return (
					<li className={styles.item} key={category.key}>
						<Button
							className={classNames(styles.pill)}
							variant={isSelected ? 'primary' : 'outline'}
							aria-pressed={isSelected}
							onClick={() => onToggleCategory(category.key)}
						>
							<span className={styles.label}>{category.name}</span>
						</Button>
					</li>
				);
			})}
		</ul>
	);
};
